// KHIS (Kenya's DHIS2) aggregate submissions (docs/plans/phase3-khis-and-quality.md §1.1):
// monthly periods, the fixed catalogue of counters Wodi can compute, mapping validation and
// cell aggregation. The mapping itself (data set, data element and category option combo
// UIDs) is data an administrator enters; nothing KHIS-specific is written here.

import { inAgeBand, type AgeBound, type AgeBoundUnit } from '../reports/age-band.ts';
import {
  MONTH_ABBREVIATIONS,
  eatDayOf,
  eatMonthRange,
  type CalendarDay,
  type InstantRange,
} from '../time.ts';

// --- Periods ---------------------------------------------------------------------------------

export interface KhisPeriod {
  readonly year: number;
  readonly month: number;
}

/** `202609` → September 2026; null for anything that isn't a real `YYYYMM`. */
export function parseKhisPeriod(text: string): KhisPeriod | null {
  const match = /^(\d{4})(\d{2})$/.exec(text);
  if (match === null) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (year < 2000 || month < 1 || month > 12) return null;
  return { year, month };
}

export function formatKhisPeriod(period: KhisPeriod): string {
  return `${String(period.year)}${String(period.month).padStart(2, '0')}`;
}

/** The month in East Africa Time, half-open. */
export function khisPeriodRange(period: KhisPeriod): InstantRange {
  return eatMonthRange(period.year, period.month);
}

/** Only a month that has ended in East Africa Time is prepared (plan K4). */
export function isCompleteKhisPeriod(period: KhisPeriod, now: Date): boolean {
  return now.getTime() >= khisPeriodRange(period).endExclusive.getTime();
}

export function lastCompleteKhisPeriod(now: Date): KhisPeriod {
  const today = eatDayOf(now);
  return today.month === 1
    ? { year: today.year - 1, month: 12 }
    : { year: today.year, month: today.month - 1 };
}

/** `Sep 2026`. */
export function khisPeriodLabel(period: KhisPeriod): string {
  return `${MONTH_ABBREVIATIONS[period.month - 1] ?? ''} ${String(period.year)}`;
}

// --- Sources ---------------------------------------------------------------------------------

/**
 * The counters Wodi computes (plan K1). Each is a set of facts with the patient's sex code
 * and age in days on the event day; never a name, an identifier or a diagnosis. Adding one
 * means a query in apps/api/src/khis/sources.ts and its tests.
 */
export const KHIS_SOURCES = {
  opd_visits: {
    title: 'Outpatient visits',
    description: 'Outpatient and walk-in visits started in the month, not cancelled.',
  },
  casualty_visits: {
    title: 'Casualty visits',
    description: 'Emergency (casualty) visits started in the month, not cancelled.',
  },
  admissions: {
    title: 'Admissions',
    description: 'Admissions in the month, not cancelled.',
  },
  discharges_alive: {
    title: 'Discharges, alive',
    description: 'Discharges in the month whose disposition is not "died".',
  },
  inpatient_deaths: {
    title: 'Inpatient deaths',
    description: 'Discharges in the month whose disposition is "died".',
  },
  lab_tests_released: {
    title: 'Lab tests released',
    description: 'Lab requests whose first report was released in the month.',
  },
} as const;

export type KhisSourceKey = keyof typeof KHIS_SOURCES;
export const KHIS_SOURCE_KEYS = Object.keys(KHIS_SOURCES) as readonly KhisSourceKey[];

export function isKhisSourceKey(value: string): value is KhisSourceKey {
  return Object.hasOwn(KHIS_SOURCES, value);
}

// --- Mapping ---------------------------------------------------------------------------------

/** The DHIS2 identifier format: a letter, then ten letters or digits. */
export function isDhis2Uid(value: string): boolean {
  return /^[A-Za-z][A-Za-z0-9]{10}$/.test(value);
}

export interface KhisMappingRowInput {
  readonly label: string;
  readonly sourceKey: string;
  /** A code from the patient sex value set; null counts every sex. */
  readonly sexCode: string | null;
  /**
   * Completed age on the event's East Africa day, as in the reports' age bands: `from` is
   * included, `below` is not ("under 5 years" is `{ from: null, below: 5 years }`). Null
   * on both sides counts every age.
   */
  readonly ageFrom: AgeBound | null;
  readonly ageBelow: AgeBound | null;
  readonly dataElementUid: string;
  readonly categoryOptionComboUid: string;
}

export const KHIS_MAPPING_PROBLEMS = {
  'khis.data_set_uid_invalid':
    'The data set UID must be a KHIS UID: a letter, then 10 letters or digits.',
  'khis.mapping_empty': 'Add at least one row before using this mapping.',
  'khis.source_unknown': 'Choose what Wodi counts for this row.',
  'khis.data_element_uid_invalid':
    'The data element UID must be a letter, then 10 letters or digits.',
  'khis.combo_uid_invalid':
    'The category option combo UID must be a letter, then 10 letters or digits.',
  'khis.age_band_invalid':
    'The age band must use whole numbers, start at 0 or more, and end after it starts.',
  'khis.label_blank': 'Give the row a label.',
  'khis.target_duplicate': 'Another row already sends to this data element and combo.',
} as const;

export type KhisMappingProblemCode = keyof typeof KHIS_MAPPING_PROBLEMS;

export interface KhisMappingProblem {
  /** The row's position, or null for the mapping as a whole. */
  readonly row: number | null;
  readonly code: KhisMappingProblemCode;
}

/** What stops a mapping from being used; empty when it is sound. */
export function validateKhisMapping(mapping: {
  readonly dataSetUid: string | null;
  readonly rows: readonly KhisMappingRowInput[];
}): KhisMappingProblem[] {
  const problems: KhisMappingProblem[] = [];
  if (mapping.dataSetUid === null || !isDhis2Uid(mapping.dataSetUid))
    problems.push({ row: null, code: 'khis.data_set_uid_invalid' });
  if (mapping.rows.length === 0) problems.push({ row: null, code: 'khis.mapping_empty' });
  problems.push(...validateKhisRows(mapping.rows));
  return problems;
}

/** Row problems only: what a draft may not be saved with. */
export function validateKhisRows(rows: readonly KhisMappingRowInput[]): KhisMappingProblem[] {
  const problems: KhisMappingProblem[] = [];
  const targets = new Set<string>();
  rows.forEach((r, i) => {
    if (!isKhisSourceKey(r.sourceKey)) problems.push({ row: i, code: 'khis.source_unknown' });
    if (!isDhis2Uid(r.dataElementUid))
      problems.push({ row: i, code: 'khis.data_element_uid_invalid' });
    if (!isDhis2Uid(r.categoryOptionComboUid))
      problems.push({ row: i, code: 'khis.combo_uid_invalid' });
    if (!ageBandIsSound(r.ageFrom, r.ageBelow))
      problems.push({ row: i, code: 'khis.age_band_invalid' });
    if (r.label.trim() === '') problems.push({ row: i, code: 'khis.label_blank' });
    const target = `${r.dataElementUid}|${r.categoryOptionComboUid}`;
    if (targets.has(target)) problems.push({ row: i, code: 'khis.target_duplicate' });
    targets.add(target);
  });
  return problems;
}

/** Rough lengths, only to check that a band ends after it starts across units. */
const APPROX_DAYS: Record<AgeBoundUnit, number> = { days: 1, months: 30.44, years: 365.25 };

function ageBandIsSound(from: AgeBound | null, below: AgeBound | null): boolean {
  const whole = (b: AgeBound | null) =>
    b === null || (Number.isSafeInteger(b.value) && b.value >= 0);
  if (!whole(from) || !whole(below)) return false;
  if (below === null) return true;
  if (below.value === 0) return false;
  if (from === null) return true;
  if (from.unit === below.unit) return below.value > from.value;
  return below.value * APPROX_DAYS[below.unit] > from.value * APPROX_DAYS[from.unit];
}

// --- Aggregation -----------------------------------------------------------------------------

export interface KhisFact {
  readonly source: KhisSourceKey;
  readonly sexCode: string;
  /** The patient's date of birth; null when it isn't recorded. */
  readonly birthDate: CalendarDay | null;
  /** The East Africa day of the event (arrival, admission, discharge, release). */
  readonly eventDay: CalendarDay;
}

export interface KhisCell {
  readonly value: number;
  /** Facts of the row's source and sex left out because the age is unknown (age-banded rows). */
  readonly unknownAgeCount: number;
}

/** One cell per mapping row, in order; zero when nothing matches. */
export function aggregateKhisCells(
  facts: readonly KhisFact[],
  rows: readonly Pick<KhisMappingRowInput, 'sourceKey' | 'sexCode' | 'ageFrom' | 'ageBelow'>[],
): KhisCell[] {
  return rows.map((r) => {
    const band = { from: r.ageFrom, below: r.ageBelow };
    const banded = r.ageFrom !== null || r.ageBelow !== null;
    let value = 0;
    let unknownAgeCount = 0;
    for (const f of facts) {
      if (f.source !== r.sourceKey) continue;
      if (r.sexCode !== null && f.sexCode !== r.sexCode) continue;
      if (banded) {
        if (f.birthDate === null) {
          unknownAgeCount += 1;
          continue;
        }
        if (!inAgeBand(f.birthDate, f.eventDay, band)) continue;
      }
      value += 1;
    }
    return { value, unknownAgeCount };
  });
}
